import { useEffect, useMemo, useState } from 'react'
import { Clock, Moon, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CapacityDay, ResourceSummary, ShiftDefinition } from '@/api/resourceCenterApi'
import { addDays, dayHeader, hoursLabel, todayIso } from '@/lib/resourceCalendar'
import { ABSENCE_STRIPES } from './profile/SplitSection'
import { shiftTone, type ShiftTone } from './shiftTones'

// The People tab's schedule views (z8nrz7f7dk): who works when, and at what
// time. Built from the same capacity rows and window as the table, so the
// three views always agree. A day's shift is known by its code; its times
// come from the center's shift definitions.

type Mode = 'roster' | 'day'

const minutes = (t: string) => {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}
/**
 * Today's date and the minutes since local midnight, read together, for the
 * timeline's "now" line — so at midnight the line moves to the new day rather
 * than back to the start of the old one.
 */
const clockNow = () => {
  const n = new Date()
  return { date: todayIso(n), min: n.getHours() * 60 + n.getMinutes() }
}
/** "07:00" → "07", "07:30" → "07:30": short enough for a roster cell. */
const shortTime = (t: string) => (t.endsWith(':00') ? t.slice(0, 2) : t)

interface Props {
  rows: ResourceSummary[]
  dates: string[]
  today: string
  daysOf: Map<string, CapacityDay[]>
  shifts: ShiftDefinition[]
  loading: boolean
  onOpen: (userId: string) => void
}

export function PeopleSchedule(props: Props) {
  const [mode, setMode] = useState<Mode>('roster')
  const { shifts } = props
  const ids = useMemo(() => shifts.map(s => s.id), [shifts])
  const byCode = useMemo(() => new Map(shifts.map(s => [s.code, s])), [shifts])
  const toneOf = (s: ShiftDefinition) => shiftTone(ids, s.id)
  // The legend lists the shifts worked in the window itself — not the extra day
  // before it that is loaded only for overnight tails.
  const usedShifts = useMemo(() => {
    const inWindow = new Set(props.dates)
    const codes = new Set<string>()
    for (const days of props.daysOf.values())
      for (const d of days) if (d.shiftCode && inWindow.has(d.date)) codes.add(d.shiftCode)
    return shifts.filter(s => codes.has(s.code))
  }, [props.daysOf, props.dates, shifts])

  return (
    <div className='space-y-3 px-4 pb-4 pt-3'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div role='tablist' aria-label='Schedule view' className='inline-flex rounded-lg bg-muted p-1'>
          {(
            [
              { value: 'roster', label: 'Roster', hint: 'Every day of the window' },
              { value: 'day', label: 'Day timeline', hint: 'One day across 24 hours' },
            ] as const
          ).map(v => (
            <button
              key={v.value}
              type='button'
              role='tab'
              aria-selected={mode === v.value}
              title={v.hint}
              onClick={() => setMode(v.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                mode === v.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
        {/* Which colour is which shift, with its hours. */}
        <div className='flex flex-wrap items-center gap-1.5 text-xs'>
          {usedShifts.map(s => (
            <span key={s.id} className='inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-0.5'>
              <span className={cn('h-2.5 w-2.5 rounded-full', toneOf(s).chip)} />
              <span className='font-mono font-semibold'>{s.code}</span>
              <span className='tabular-nums text-muted-foreground'>
                {s.startTime}–{s.endTime}
              </span>
            </span>
          ))}
          <span className='inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-0.5 text-muted-foreground'>
            <span className='h-2.5 w-3 rounded-sm border' style={{ backgroundImage: ABSENCE_STRIPES }} /> Away
          </span>
        </div>
      </div>

      {mode === 'roster' ? (
        <Roster {...props} byCode={byCode} toneOf={toneOf} />
      ) : (
        <DayTimeline {...props} byCode={byCode} toneOf={toneOf} />
      )}
    </div>
  )
}

// ── Roster ───────────────────────────────────────────────────────────────────

/** People down, days across: each cell the shift worked, with its times. */
function Roster({
  rows,
  dates,
  today,
  daysOf,
  loading,
  onOpen,
  byCode,
  toneOf,
}: Props & { byCode: Map<string, ShiftDefinition>; toneOf: (s: ShiftDefinition) => ShiftTone }) {
  const dayOf = (userId: string, date: string) => daysOf.get(userId)?.find(d => d.date === date)
  const onCount = (date: string) => rows.filter(r => dayOf(r.userId, date)?.state === 'ON').length

  return (
    <div className='overflow-x-persistent rounded-xl border'>
      <table className='w-full border-separate border-spacing-0 text-sm'>
        <thead>
          <tr>
            <th className='sticky left-0 z-20 min-w-[13rem] border-b bg-muted/60 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur'>
              Person
            </th>
            {dates.map(d => {
              const h = dayHeader(d)
              return (
                <th
                  key={d}
                  className={cn(
                    'min-w-[5.5rem] border-b px-1 py-1.5 text-center font-medium',
                    h.weekend ? 'bg-muted/70' : 'bg-muted/40',
                    d === today && 'bg-amber-100 text-amber-900',
                  )}
                >
                  <span className='block text-[10px] uppercase tracking-wide opacity-70'>{h.dow}</span>
                  <span className='block text-xs tabular-nums'>{h.md}</span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.userId} className='group'>
              <td className='sticky left-0 z-10 border-b bg-background px-3 py-1.5 group-hover:bg-muted/40'>
                <button type='button' onClick={() => onOpen(r.userId)} className='min-w-0 text-left'>
                  <span className='block truncate font-medium hover:text-primary'>{r.name}</span>
                  <span className='block truncate text-[11px] text-muted-foreground'>
                    {r.teams.map(t => t.name).join(', ') || r.groups.find(g => g.isPrimary)?.name || '—'}
                  </span>
                </button>
              </td>
              {dates.map(d => (
                <td
                  key={d}
                  className={cn('border-b px-1 py-1 group-hover:bg-muted/20', d === today && 'bg-amber-50/60')}
                >
                  <RosterCell day={dayOf(r.userId, d)} byCode={byCode} toneOf={toneOf} loading={loading} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className='sticky left-0 z-10 bg-muted/60 px-3 py-2 text-xs font-semibold text-muted-foreground'>
              <span className='flex items-center gap-1.5'>
                <Users className='h-3.5 w-3.5' /> On shift
              </span>
            </td>
            {dates.map(d => (
              <td key={d} className='bg-muted/40 px-1 py-2 text-center text-xs font-semibold tabular-nums'>
                {onCount(d)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function RosterCell({
  day,
  byCode,
  toneOf,
  loading,
}: {
  day: CapacityDay | undefined
  byCode: Map<string, ShiftDefinition>
  toneOf: (s: ShiftDefinition) => ShiftTone
  loading: boolean
}) {
  const base = 'flex h-12 w-full flex-col items-center justify-center rounded-lg text-center leading-tight'
  if (!day) return <div className={cn(base, 'bg-muted/30', loading && 'animate-pulse')} />
  if (day.state === 'NOT_STARTED') return <div className={cn(base, 'border border-dashed text-muted-foreground/50')}>—</div>
  if (day.state === 'OFF') return <div className={cn(base, 'text-[11px] font-medium text-muted-foreground/60')}>OFF</div>
  const shift = day.shiftCode ? byCode.get(day.shiftCode) : undefined
  if (day.state === 'UNAVAILABLE') {
    return (
      <div
        className={cn(base, 'border border-rose-200 text-[11px] font-semibold text-rose-700')}
        style={{ backgroundImage: ABSENCE_STRIPES }}
        title={`Away${day.uaCode ? ` (${day.uaCode})` : ''}${shift ? ` — would have worked ${shift.code} ${shift.startTime}–${shift.endTime}` : ''}`}
      >
        {day.uaCode ?? 'Away'}
      </div>
    )
  }
  const partlyAway = day.uaHrs > 0
  return (
    <div
      className={cn(base, shift ? toneOf(shift).cell : 'bg-slate-500 text-white', 'shadow-sm')}
      title={
        shift
          ? `${shift.name} · ${shift.startTime}–${shift.endTime} · ${hoursLabel(day.shiftHrs)} h${partlyAway ? ` · ${day.uaCode ?? 'away'} ${hoursLabel(day.uaHrs)} h` : ''}`
          : undefined
      }
    >
      <span className='flex items-center gap-1 font-mono text-[12px] font-bold'>
        {day.shiftCode ?? 'ON'}
        {shift?.wrapsMidnight && <Moon className='h-3 w-3 opacity-80' />}
      </span>
      {shift && (
        <span className='text-[10px] tabular-nums opacity-90'>
          {shortTime(shift.startTime)}–{shortTime(shift.endTime)}
        </span>
      )}
      {partlyAway && (
        <span className='mt-0.5 rounded bg-white/25 px-1 text-[9px] font-semibold'>
          −{hoursLabel(day.uaHrs)}h {day.uaCode ?? ''}
        </span>
      )}
    </div>
  )
}

// ── Day timeline ─────────────────────────────────────────────────────────────

interface Bar {
  shift: ShiftDefinition
  from: number
  to: number
  /** The tail of yesterday's overnight shift. */
  carried: boolean
}

/**
 * One day across 24 hours: a bar per person from when they start to when they
 * stop. An overnight shift runs to midnight here and its tail shows on the
 * next day from 00:00, so a night worker is visible on both mornings.
 */
function DayTimeline({
  rows,
  dates,
  today,
  daysOf,
  onOpen,
  byCode,
  toneOf,
}: Props & { byCode: Map<string, ShiftDefinition>; toneOf: (s: ShiftDefinition) => ShiftTone }) {
  const [date, setDate] = useState(dates.includes(today) ? today : dates[0])
  // The window moved: follow it to today, or its first day.
  useEffect(() => {
    if (!dates.includes(date)) setDate(dates.includes(today) ? today : dates[0])
  }, [dates, date, today])

  const dayOf = (userId: string, d: string) => daysOf.get(userId)?.find(x => x.date === d)
  const barsFor = (userId: string): Bar[] => {
    const bars: Bar[] = []
    const yesterday = dayOf(userId, addDays(date, -1))
    const prev = yesterday?.state === 'ON' && yesterday.shiftCode ? byCode.get(yesterday.shiftCode) : undefined
    if (prev?.wrapsMidnight) bars.push({ shift: prev, from: 0, to: minutes(prev.endTime), carried: true })
    const d = dayOf(userId, date)
    const own = d?.state === 'ON' && d.shiftCode ? byCode.get(d.shiftCode) : undefined
    if (own) {
      const from = minutes(own.startTime)
      bars.push({ shift: own, from, to: own.wrapsMidnight ? 1440 : minutes(own.endTime), carried: false })
    }
    return bars
  }

  // Working people first, by start time; then away, then off.
  const people = rows
    .map(r => ({ r, bars: barsFor(r.userId), day: dayOf(r.userId, date) }))
    .sort((a, b) => {
      const rank = (x: typeof a) => (x.bars.length ? 0 : x.day?.state === 'UNAVAILABLE' ? 1 : 2)
      const start = (x: typeof a) => x.bars.find(bar => !bar.carried)?.from ?? x.bars[0]?.from ?? 9999
      return rank(a) - rank(b) || start(a) - start(b) || a.r.name.localeCompare(b.r.name)
    })
  const working = people.filter(p => p.bars.length > 0).length
  const totalHours = people.reduce((n, p) => n + p.bars.reduce((m, b) => m + (b.to - b.from) / 60, 0), 0)

  // The "now" line keeps moving while the page stays open, and crosses into
  // the next day at midnight without waiting for the parent to re-render.
  const [clock, setClock] = useState(clockNow)
  useEffect(() => {
    const id = setInterval(() => setClock(clockNow()), 60_000)
    return () => clearInterval(id)
  }, [])
  const pct = (m: number) => `${(m / 1440) * 100}%`
  const HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24]

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='flex flex-wrap gap-1' role='radiogroup' aria-label='Day'>
          {dates.map(d => {
            const h = dayHeader(d)
            return (
              <button
                key={d}
                type='button'
                role='radio'
                aria-checked={d === date}
                onClick={() => setDate(d)}
                className={cn(
                  'flex w-12 flex-col items-center rounded-lg border px-1 py-1 text-[11px] leading-tight transition-colors',
                  d === date ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted',
                  d !== date && d === today && 'border-amber-400',
                )}
              >
                <span className='opacity-80'>{h.dow.slice(0, 3)}</span>
                <span className='font-semibold tabular-nums'>{h.md}</span>
              </button>
            )
          })}
        </div>
        <div className='flex items-center gap-1.5 text-xs'>
          <span className='inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-primary'>
            <Users className='h-3.5 w-3.5' /> <span className='font-semibold tabular-nums'>{working}</span> working
          </span>
          <span className='inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-muted-foreground'>
            <Clock className='h-3.5 w-3.5' /> <span className='font-semibold tabular-nums text-foreground'>{hoursLabel(totalHours)} h</span> on site
          </span>
        </div>
      </div>

      <div className='overflow-x-persistent rounded-xl border'>
        <div className='min-w-[44rem]'>
          {/* Hour ruler */}
          <div className='grid grid-cols-[13rem_1fr] border-b bg-muted/40'>
            <div className='px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Person</div>
            <div className='relative h-8'>
              {HOURS.map(h => (
                <span
                  key={h}
                  className='absolute top-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground first:translate-x-0 last:-translate-x-full'
                  style={{ left: pct(h * 60) }}
                >
                  {String(h).padStart(2, '0')}:00
                </span>
              ))}
            </div>
          </div>

          {people.map(({ r, bars, day }) => (
            <div key={r.userId} className='group grid grid-cols-[13rem_1fr] border-b last:border-b-0 hover:bg-muted/30'>
              <button type='button' onClick={() => onOpen(r.userId)} className='min-w-0 px-3 py-2 text-left'>
                <span className='block truncate text-sm font-medium hover:text-primary'>{r.name}</span>
                <span className='block truncate text-[11px] text-muted-foreground'>
                  {bars.length
                    ? bars
                        .map(b => `${b.shift.code} ${b.carried ? `until ${b.shift.endTime}` : `${b.shift.startTime}–${b.shift.endTime}`}`)
                        .join(' · ')
                    : day?.state === 'UNAVAILABLE'
                      ? `Away${day.uaCode ? ` (${day.uaCode})` : ''}`
                      : day?.state === 'NOT_STARTED'
                        ? 'Not started yet'
                        : 'Off'}
                </span>
              </button>
              <div className='relative my-1.5 mr-3 h-9 rounded-md bg-muted/40'>
                {/* Gridlines every 3 hours, and "now" on today. */}
                {HOURS.slice(1, -1).map(h => (
                  <span key={h} className='absolute top-0 h-full w-px bg-border' style={{ left: pct(h * 60) }} />
                ))}
                {date === clock.date && (
                  <span className='absolute -top-1 z-10 h-[calc(100%+0.5rem)] w-0.5 bg-rose-500' style={{ left: pct(clock.min) }} />
                )}
                {day?.state === 'UNAVAILABLE' && (
                  <span className='absolute inset-0 rounded-md' style={{ backgroundImage: ABSENCE_STRIPES }} />
                )}
                {bars.map(b => (
                  <span
                    key={`${b.shift.id}-${b.carried}`}
                    title={`${b.shift.name} · ${b.shift.startTime}–${b.shift.endTime}`}
                    className={cn(
                      'absolute top-1 flex h-7 items-center gap-1 overflow-hidden rounded-md px-2 text-[11px] font-semibold shadow-sm',
                      toneOf(b.shift).cell,
                      b.carried && 'opacity-70',
                    )}
                    style={{ left: pct(b.from), width: pct(b.to - b.from) }}
                  >
                    <span className='font-mono'>{b.shift.code}</span>
                    <span className='truncate font-normal tabular-nums opacity-90'>
                      {b.carried ? `→ ${b.shift.endTime}` : `${b.shift.startTime}–${b.shift.endTime}`}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
